/**
 * Outbound contact attempts — distinct numbers by dial count (1–10, 11+), success vs open.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { buildOutboundAnalyticsParams, type WidgetFilterConfig } from '../../lib/widgetScope';
import { formatAnalyticsCount } from '../../lib/outboundAnalyticsDisplay';

type Bucket = { bucket: string; contacts: number; success: number; open: number };

type ApiResponse = {
  range: string;
  date_from: string;
  date_to: string;
  total_outbound_dials: number;
  distinct_contacts: number;
  distinct_success: number;
  distinct_open: number;
  buckets: Bucket[];
};

interface Props {
  config?: WidgetFilterConfig;
}

const REFRESH_MS = 60_000;
const SUCCESS_COLOR = '#22c55e';
const OPEN_COLOR = '#eab308';

export default function WidgetOutboundContactAttempts({ config }: Props) {
  const { getToken } = useAuth();
  const [data, setData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const params = buildOutboundAnalyticsParams(config);
    try {
      const res = await fetch(`${base}/api/analytics/outbound-contacts?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        setError('Admin access required');
        setData(null);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError((body as { error?: string }).error || `HTTP ${res.status}`);
        setData(null);
        return;
      }
      setData((await res.json()) as ApiResponse);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [getToken, config]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchData().then(() => {
      if (cancelled) return;
    });
    const id = window.setInterval(() => void fetchData(), REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [fetchData]);

  const chartRows = useMemo(() => {
    if (!data?.buckets.length) return [];
    return data.buckets.filter((b) => b.contacts > 0);
  }, [data]);

  const hasData = (data?.total_outbound_dials ?? 0) > 0 || (data?.distinct_contacts ?? 0) > 0;

  const tooltipStyle = {
    background: 'var(--widget-bg)',
    border: '1px solid var(--header-border)',
    borderRadius: 6,
    fontSize: '0.75rem',
  };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '0.35rem 0.5rem',
        containerType: 'size',
      }}
    >
      {loading && !data && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center' }}>
          Loading…
        </p>
      )}
      {error && (
        <p style={{ color: 'var(--error)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center' }}>
          {error}
        </p>
      )}
      {!loading && !error && !hasData && (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 'auto 0', textAlign: 'center', lineHeight: 1.45 }}>
          No outbound contact data for this period yet. Counts build from agent.wrap webhooks.
        </p>
      )}
      {!error && hasData && (
        <>
          <div style={{ flex: 1, minHeight: 80 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="bucket" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(value, name) => [
                    formatAnalyticsCount(Number(value ?? 0)),
                    name === 'success' ? 'Success' : 'Open',
                  ]}
                  labelFormatter={(label) => `${label} attempt${label === '1' ? '' : 's'}`}
                />
                <Legend wrapperStyle={{ fontSize: '0.65rem' }} />
                <Bar dataKey="success" name="Success" stackId="a" fill={SUCCESS_COLOR} />
                <Bar dataKey="open" name="Open" stackId="a" fill={OPEN_COLOR} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p
            style={{
              margin: '0.25rem 0 0',
              fontSize: '0.7rem',
              color: 'var(--text-muted)',
              textAlign: 'right',
              lineHeight: 1.35,
            }}
          >
            {formatAnalyticsCount(data!.total_outbound_dials)} outbound dials ·{' '}
            {formatAnalyticsCount(data!.distinct_contacts)} distinct numbers (
            {formatAnalyticsCount(data!.distinct_success)} success · {formatAnalyticsCount(data!.distinct_open)} open)
          </p>
        </>
      )}
    </div>
  );
}
